// Pragas e doencas da especie. Incluir, alterar ou remover reabre a revisao da ficha.
import { useState } from "react";
import type { SpeciesPest, SpeciesPestFields, SpeciesProfileCatalogs } from "@/types/species-profile";
import { label } from "../profileFormat";
import { Section } from "./ProfileView";

const SEVERITY_TONE: Record<string, string> = {
  BAIXA: "bg-primary-50 text-primary-700",
  MEDIA: "bg-warning-50 text-warning-700",
  ALTA: "bg-danger-50 text-danger-700",
};

const BLANK: SpeciesPestFields = {
  common_name: "", agent_scientific_name: null, agent_type: "INSETO", affected_parts: [],
  symptoms: null, severity: null, management: null,
};

const inputCls =
  "w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none";

function PestForm({ initial, catalogs, busy, onCancel, onSave }: {
  initial: SpeciesPestFields;
  catalogs: SpeciesProfileCatalogs;
  busy: boolean;
  onCancel: () => void;
  onSave: (v: SpeciesPestFields) => void;
}) {
  const [v, setV] = useState<SpeciesPestFields>(initial);
  const set = <K extends keyof SpeciesPestFields>(k: K, value: SpeciesPestFields[K]) => setV((x) => ({ ...x, [k]: value }));
  const text = (k: "agent_scientific_name" | "symptoms" | "management") => (e: { target: { value: string } }) =>
    set(k, e.target.value === "" ? null : e.target.value);

  return (
    <div className="rounded-lg border border-primary-200 bg-primary-50/30 p-4 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-xs font-medium text-foreground-600">
          Nome popular da praga/doença *
          <input type="text" className={`${inputCls} mt-1`} value={v.common_name} maxLength={160}
            onChange={(e) => set("common_name", e.target.value)} />
        </label>
        <label className="text-xs font-medium text-foreground-600">
          Agente (nome científico)
          <input type="text" className={`${inputCls} mt-1`} value={v.agent_scientific_name ?? ""} maxLength={180}
            onChange={text("agent_scientific_name")} />
        </label>
        <label className="text-xs font-medium text-foreground-600">
          Tipo de agente
          <select className={`${inputCls} mt-1`} value={v.agent_type} onChange={(e) => set("agent_type", e.target.value)}>
            {Object.entries(catalogs.agent_types).map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </select>
        </label>
        <label className="text-xs font-medium text-foreground-600">
          Severidade
          <select className={`${inputCls} mt-1`} value={v.severity ?? ""} onChange={(e) => set("severity", e.target.value || null)}>
            <option value="">Não informada</option>
            {Object.entries(catalogs.levels).map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </select>
        </label>
      </div>
      <div>
        <span className="text-xs font-medium text-foreground-600">Partes afetadas</span>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {Object.entries(catalogs.plant_parts).map(([c, n]) => {
            const on = v.affected_parts.includes(c);
            return (
              <button key={c} type="button" aria-pressed={on}
                onClick={() => set("affected_parts", on ? v.affected_parts.filter((x) => x !== c) : [...v.affected_parts, c])}
                className={`badge border cursor-pointer ${on ? "bg-primary-500 text-white border-primary-500" : "bg-white text-foreground-600 border-background-300"}`}>
                {n}
              </button>
            );
          })}
        </div>
      </div>
      <label className="block text-xs font-medium text-foreground-600">
        Sintomas
        <textarea rows={2} className={`${inputCls} mt-1`} value={v.symptoms ?? ""} onChange={text("symptoms")} />
      </label>
      <label className="block text-xs font-medium text-foreground-600">
        Manejo
        <textarea rows={2} className={`${inputCls} mt-1`} value={v.management ?? ""} onChange={text("management")} />
      </label>
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-outline" onClick={onCancel} disabled={busy}>Cancelar</button>
        <button type="button" className="btn-primary disabled:opacity-50" disabled={busy || v.common_name.trim().length < 2}
          onClick={() => onSave({ ...v, common_name: v.common_name.trim() })}>
          {busy ? "Salvando..." : "Salvar"}
        </button>
      </div>
    </div>
  );
}

interface Props {
  pests: SpeciesPest[];
  catalogs: SpeciesProfileCatalogs;
  canEdit: boolean;
  busy: boolean;
  onAdd: (v: SpeciesPestFields) => Promise<boolean>;
  onUpdate: (id: number, v: SpeciesPestFields) => Promise<boolean>;
  onDelete: (id: number) => Promise<boolean>;
}

export default function PestsSection({ pests, catalogs, canEdit, busy, onAdd, onUpdate, onDelete }: Props) {
  const [editing, setEditing] = useState<number | "new" | null>(null);

  return (
    <Section title={`Pragas e doenças (${pests.length})`} icon="ri-bug-line">
      <div className="space-y-3">
        {pests.length === 0 && editing !== "new" && (
          <p className="text-sm text-foreground-400">Nenhuma praga ou doença registrada.</p>
        )}
        {pests.map((p) =>
          editing === p.id ? (
            <PestForm key={p.id} initial={p} catalogs={catalogs} busy={busy} onCancel={() => setEditing(null)}
              onSave={async (v) => { if (await onUpdate(p.id, v)) setEditing(null); }} />
          ) : (
            <article key={p.id} className="rounded-lg border border-background-200 p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900">{p.common_name}</h3>
                  <p className="text-xs text-foreground-500">
                    {label(catalogs.agent_types, p.agent_type)}
                    {p.agent_scientific_name && <> · <i>{p.agent_scientific_name}</i></>}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {p.severity && <span className={`badge ${SEVERITY_TONE[p.severity] ?? ""}`}>Severidade {label(catalogs.levels, p.severity).toLowerCase()}</span>}
                  {canEdit && editing === null && (
                    <>
                      <button type="button" className="text-foreground-500 hover:text-primary-700 p-1" title="Editar" aria-label={`Editar ${p.common_name}`}
                        onClick={() => setEditing(p.id)}><i className="ri-pencil-line" /></button>
                      <button type="button" className="text-foreground-500 hover:text-danger-700 p-1" title="Remover" aria-label={`Remover ${p.common_name}`}
                        disabled={busy}
                        onClick={() => { if (window.confirm(`Remover "${p.common_name}" da ficha?`)) void onDelete(p.id); }}>
                        <i className="ri-delete-bin-line" /></button>
                    </>
                  )}
                </div>
              </div>
              {p.affected_parts.length > 0 && (
                <p className="text-xs text-foreground-600 mt-2">
                  <span className="text-foreground-500">Partes afetadas: </span>
                  {p.affected_parts.map((x) => label(catalogs.plant_parts, x)).join(", ")}
                </p>
              )}
              {p.symptoms && <p className="text-sm text-foreground-700 mt-2"><span className="text-foreground-500">Sintomas: </span>{p.symptoms}</p>}
              {p.management && <p className="text-sm text-foreground-700 mt-1"><span className="text-foreground-500">Manejo: </span>{p.management}</p>}
            </article>
          )
        )}
        {editing === "new" && (
          <PestForm initial={BLANK} catalogs={catalogs} busy={busy} onCancel={() => setEditing(null)}
            onSave={async (v) => { if (await onAdd(v)) setEditing(null); }} />
        )}
        {canEdit && editing === null && (
          <button type="button" className="btn-outline" onClick={() => setEditing("new")}>
            <i className="ri-add-line" /> Adicionar praga ou doença
          </button>
        )}
      </div>
    </Section>
  );
}
